import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, IconButton, Paper, Typography, Slide } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GetAppIcon from '@mui/icons-material/GetApp';
import IosShareIcon from '@mui/icons-material/IosShare';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import { setShowInstallPrompt, setInstalled, setDeferredPrompt } from '../actions/pwa';

const ua = navigator.userAgent.toLowerCase();
const isIos = /iphone|ipad|ipod/.test(ua);
const isIosChrome = isIos && /crios/.test(ua);
const isIosSafari = isIos && !isIosChrome && /safari/.test(ua);

const InstallPWA = () => {
  const dispatch = useDispatch();
  const showInstallPrompt = useSelector((state) => state.pwa.showInstallPrompt);
  const deferredPrompt = useSelector((state) => state.pwa.deferredPrompt);
  const [showIosBanner, setShowIosBanner] = useState(false);

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) {
      console.log('PWA: App is already installed');
      dispatch(setInstalled(true));
      return;
    }

    if (isIos) {
      const dismissed = localStorage.getItem('pwa-ios-dismissed');
      if (!dismissed) {
        setTimeout(() => setShowIosBanner(true), 3000);
      }
      return;
    }

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      console.log('PWA: Install prompt available');
      dispatch(setDeferredPrompt(e));
      setTimeout(() => {
        dispatch(setShowInstallPrompt(true));
      }, 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    window.addEventListener('appinstalled', () => {
      console.log('PWA: App was installed');
      dispatch(setInstalled(true));
      dispatch(setShowInstallPrompt(false));
      dispatch(setDeferredPrompt(null));
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [dispatch]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    console.log(`PWA: User ${outcome} the install prompt`);
    dispatch(setShowInstallPrompt(false));
    dispatch(setDeferredPrompt(null));
  };

  const handleDismiss = () => dispatch(setShowInstallPrompt(false));

  const handleIosDismiss = () => {
    localStorage.setItem('pwa-ios-dismissed', '1');
    setShowIosBanner(false);
  };

  const paperSx = {
    position: 'fixed',
    bottom: 16,
    left: 16,
    right: 16,
    p: 2.5,
    zIndex: 1300,
    maxWidth: 450,
    mx: 'auto',
    borderTop: '4px solid #2089BE',
    backgroundColor: '#fff',
  };

  if (showIosBanner) {
    return (
      <Slide direction="up" in mountOnEnter unmountOnExit>
        <Paper elevation={6} sx={paperSx}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Box sx={{ backgroundColor: '#022D41', borderRadius: 1, p: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <GetAppIcon sx={{ color: '#fff', fontSize: 28 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: '#022D41' }}>
                Install Airswift Digital
              </Typography>
              {isIosSafari ? (
                <Box sx={{ mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">Tap</Typography>
                    <IosShareIcon sx={{ color: '#2089BE', fontSize: 20 }} />
                    <Typography variant="body2" color="text.secondary">to share</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">Then tap</Typography>
                    <AddBoxOutlinedIcon sx={{ color: '#2089BE', fontSize: 20 }} />
                    <Typography variant="body2" color="text.secondary">
                      <strong>Add to Home Screen</strong>
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Tap <strong>⋮</strong> at the top right, then <strong>Add to Home Screen</strong>
                </Typography>
              )}
              <Typography variant="caption" color="text.disabled">
                Get quick access and work offline.
              </Typography>
            </Box>
            <IconButton size="small" onClick={handleIosDismiss} sx={{ mt: -1, mr: -1, color: '#022D41' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Paper>
      </Slide>
    );
  }

  return (
    <Slide direction="up" in={showInstallPrompt} mountOnEnter unmountOnExit>
      <Paper elevation={6} sx={paperSx}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Box sx={{ backgroundColor: '#022D41', borderRadius: 1, p: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <GetAppIcon sx={{ color: '#fff', fontSize: 28 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: '#022D41' }}>
              Install Airswift Digital
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 2 }}>
              Get quick access and work offline. Install our app for the best experience.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button
                variant="contained"
                size="medium"
                onClick={handleInstallClick}
                sx={{ backgroundColor: '#2089BE', '&:hover': { backgroundColor: '#022D41' } }}
              >
                Install Now
              </Button>
              <Button
                variant="outlined"
                size="medium"
                onClick={handleDismiss}
                sx={{ borderColor: '#022D41', color: '#022D41', '&:hover': { borderColor: '#2089BE', backgroundColor: 'rgba(32, 137, 190, 0.04)' } }}
              >
                Not Now
              </Button>
            </Box>
          </Box>
          <IconButton size="small" onClick={handleDismiss} sx={{ mt: -1, mr: -1, color: '#022D41' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Paper>
    </Slide>
  );
};

export default InstallPWA;